import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import StyleIcon from '@mui/icons-material/Style';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BoltIcon from '@mui/icons-material/Bolt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ArenaDemo from './demo/ArenaDemo';
import TopWinners from './demo/TopWinners';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import RaffleFooter from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import '../Home/DynamicHomepage/components/Landing/landing.css';

/* The starter pack's headline value. Hardcoded ON PURPOSE and only here: this page is public,
   so it has no authenticated endpoint to read the package from the way the member-portal pages
   do. If package 55 is ever repriced, this is the one line to change — and the member-facing
   pages will already have followed on their own, because they read the real price from the API. */
const PACK_VALUE = '$40';

const mono = 'ui-monospace, Menlo, Consolas, monospace';

const kickerSx = {
  font: `800 11px ${mono}`,
  letterSpacing: '0.32em',
  textTransform: 'uppercase' as const,
  color: '#38e8ff'
};

const ctaSx = {
  px: 4,
  py: 1.35,
  borderRadius: 999,
  fontWeight: 900,
  fontSize: 16,
  textTransform: 'none' as const,
  color: '#04121a',
  background: 'linear-gradient(180deg,#ffdd6e,#ffcf40)',
  boxShadow: '0 14px 34px -12px rgba(255,207,64,0.7)',
  '&:hover': { background: 'linear-gradient(180deg,#ffe790,#ffd85c)' }
};

const PILLARS = [
  {
    icon: <StorefrontIcon />,
    title: 'Trade what you pull',
    body: 'Cards you do not need are worth something. Buy, sell and trade them with other members on the in-portal marketplace.'
  },
  {
    icon: <StyleIcon />,
    title: 'Decks you actually own',
    body: 'Every card is a collectible in your collection — not a licence that disappears when you stop playing. Build a deck from what you own, change it whenever you like.'
  },
  {
    icon: <BoltIcon />,
    title: 'Fast, lane-based duels',
    body: 'Commit cards to lanes, win initiative, and cut your opponent down. A match runs in minutes, straight in the browser — nothing to install.'
  },
  {
    icon: <EmojiEventsIcon />,
    title: 'A weekly leaderboard with real prizes',
    body: 'Ranked matches move your rating and your weekly points. The top of the leaderboard takes a share of the INC prize pool every week.'
  }
];

const STEPS = [
  'Create your account — it takes a minute.',
  'Claim your free Relic Starter Pack and open it.',
  'Your deck is built for you. Jump into the TCG and play.'
];

const TradingCardGame = () => {
  return (
    <Box
      sx={{
        background: '#070c13',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <SiteHeader />

      {/* ---------------- who has won what ---------------- */}
      <TopWinners />

      {/* ---------------- play it now ----------------
          Before the offer, not after it: someone who has just played a match is a
          different reader of "sign up for a free pack" than someone who has only been
          told about the game. */}
      <Box component="section" sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 5, md: 7 } }}>
        <Box sx={{ maxWidth: 860, mx: 'auto' }}>
          <ArenaDemo packValue={PACK_VALUE} />
        </Box>
      </Box>

      {/* ---------------- the offer ----------------
          id is the scroll target for the winners strip's "Learn more" — that panel
          explains the mechanic and hands the reader down here for the pack, rather
          than firing them at /sign-up before they know what they are signing up
          for. scrollMarginTop keeps the heading clear of the fixed site header. */}
      <Box
        component="section"
        id="starter-pack"
        sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 5, md: 7 }, scrollMarginTop: { xs: 80, md: 96 } }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            maxWidth: 900,
            mx: 'auto',
            textAlign: 'center',
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 5.5 },
            borderRadius: '22px',
            border: '1px solid rgba(255,207,64,0.35)',
            background:
              'linear-gradient(150deg, rgba(255,207,64,0.12), rgba(56,232,255,0.08))',
            boxShadow: '0 26px 70px -34px rgba(255,207,64,0.55)'
          }}
        >
          <Inventory2Icon sx={{ fontSize: 38, color: '#ffcf40' }} />
          <Typography sx={{ ...kickerSx, color: '#ffcf40', mt: 1 }}>
            New members
          </Typography>

          <Typography
            component="h2"
            sx={{
              mt: 1.25,
              color: '#fff',
              fontWeight: 900,
              fontSize: { xs: 25, md: 34 },
              lineHeight: 1.15,
              textWrap: 'balance'
            }}
          >
            Sign up now and get a free Relic Starter Pack
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: 1.25
            }}
          >
            <Typography
              sx={{
                color: '#8ea3b3',
                textDecoration: 'line-through',
                textDecorationColor: 'rgba(255,255,255,0.4)',
                fontSize: 20,
                fontWeight: 600
              }}
            >
              {PACK_VALUE} AUD
            </Typography>
            <Typography sx={{ color: '#ffcf40', fontSize: 27, fontWeight: 900, lineHeight: 1 }}>
              FREE
            </Typography>
          </Box>

          <Typography
            sx={{ mt: 1.75, mx: 'auto', maxWidth: 520, color: '#c7d6e0', fontSize: 15.5, lineHeight: 1.6 }}
          >
            A complete 50-card Relic Starter Pack — enough to build a deck and start
            playing the moment you open it.
          </Typography>

          <Button component={Link} to="/sign-up" sx={{ ...ctaSx, mt: 3 }}>
            Create your free account
          </Button>

          {/* The offer is for verified members. Said quietly and after the CTA rather than in
              the headline — but said, because a value claim on a public page should not be a
              surprise later. The verification step itself is explained in full on /member-portal/kyc
              before anyone submits anything. */}
          <Typography sx={{ mt: 1.75, color: '#8ea3b3', font: `600 12.5px ${mono}` }}>
            Free for verified members · one per account
          </Typography>
        </Box>
      </Box>

      {/* ---------------- hero ---------------- */}
      <Box component="section" sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box
          className="inc-glow"
          sx={{
            /* Tied to the hero's own box (inset: 0), NOT a fixed 480px tall.
               The hero is ~280px on a normal desktop, so a 480px glow hung well
               below it and the section's overflow:hidden cut it where the gradient
               still had a third of its opacity left — a hard horizontal rule right
               above the winners strip. Sizing the gradient in percentages of a box
               that IS the hero means the fade is always spent before any edge,
               whatever the headline wraps to. Centred at 45% rather than up near
               the top for the same reason in the other direction: 0.72 x 56% is
               40%, so the glow is gone by 5% from the top and 85% down. */
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(ellipse 42% 56% at 50% 45%, rgba(56,232,255,0.16), transparent 72%)'
          }}
        />

        <Box
          sx={{
            position: 'relative',
            maxWidth: 1100,
            mx: 'auto',
            px: { xs: 2.5, md: 3 },
            pt: { xs: 4, md: 7 },
            pb: { xs: 4, md: 6 },
            textAlign: 'center'
          }}
        >
          <Typography sx={kickerSx}>Trading Card Game</Typography>
          <Typography
            component="h1"
            sx={{
              mt: 1.25,
              color: '#fff',
              fontWeight: 900,
              fontSize: { xs: 32, md: 52 },
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              textWrap: 'balance'
            }}
          >
            The INCRYPT Trading Card Game
          </Typography>
          <Typography
            sx={{
              mt: 2,
              mx: 'auto',
              maxWidth: 620,
              color: '#c7d6e0',
              fontSize: { xs: 15.5, md: 17 },
              lineHeight: 1.6
            }}
          >
            Collect the cards. Build the deck. Play it against other members and
            climb a weekly leaderboard with a real prize pool — free to play, right
            in your browser.
          </Typography>
        </Box>
      </Box>

      {/* ---------------- what it is ---------------- */}
      <Box component="section" sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 5, md: 7 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography sx={{ ...kickerSx, textAlign: 'center' }}>Why play</Typography>
          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: { xs: 2, md: 2.5 }
            }}
          >
            {PILLARS.map((p) => (
              <Box
                key={p.title}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: '16px',
                  border: '1px solid rgba(140,170,190,0.16)',
                  background: 'linear-gradient(180deg, rgba(15,22,32,0.9), rgba(9,14,21,0.9))'
                }}
              >
                <Box sx={{ color: '#38e8ff', '& svg': { fontSize: 26 } }}>{p.icon}</Box>
                <Typography sx={{ mt: 1.25, color: '#fff', fontWeight: 800, fontSize: 18 }}>
                  {p.title}
                </Typography>
                <Typography sx={{ mt: 0.75, color: '#a8bccb', fontSize: 15, lineHeight: 1.6 }}>
                  {p.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ---------------- getting started ---------------- */}
      <Box component="section" sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: 760, mx: 'auto', textAlign: 'center' }}>
          <Typography sx={kickerSx}>Getting started</Typography>
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {STEPS.map((step, i) => (
              <Box
                key={step}
                sx={{ display: 'flex', gap: 2, alignItems: 'center', textAlign: 'left' }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    border: '1px solid rgba(56,232,255,0.4)',
                    color: '#38e8ff',
                    font: `800 13px ${mono}`
                  }}
                >
                  {i + 1}
                </Box>
                <Typography sx={{ color: '#c7d6e0', fontSize: 15.5 }}>{step}</Typography>
              </Box>
            ))}
          </Box>

          <Button component={Link} to="/sign-up" sx={{ ...ctaSx, mt: 4 }}>
            Sign up and claim your pack
          </Button>
        </Box>
      </Box>

      <RaffleFooter />
    </Box>
  );
};

export default TradingCardGame;
